/*
  Custom cart progress bar — client-side layer.

  Full approach and rationale: see the header comment in
  snippets/cart-progress-bar.liquid. Short version:

  Liquid is the source of truth. This script is deliberately NOT a
  reimplementation of the bar; it covers only the three things Liquid cannot:

    1. Cart mutations that bypass Dawn's pub/sub. The gift apps inject their
       free line item over AJAX, so changes inside the item list trigger a
       refresh. Dawn's own flows already re-render the whole drawer, so we skip
       fetching there to avoid a duplicate request against markup that is about
       to be discarded.

    2. Presentment currency conversion. Discounty converts its campaign
       threshold with Shopify's live exchange rate, and that rate is not
       exposed to Liquid — only as window.Shopify.currency.rate. In the shop
       currency this script does nothing to the numbers; in any other currency
       it re-derives thresholds and re-renders the copy.

    3. Hiding the app's widget, which Discounty re-injects on every cart update.

  The bar is a plain <div>, not a custom element, so there is no
  connectedCallback to hook. Dawn replaces the whole drawer by assigning
  innerHTML, which silently swaps our element for a new one. Instead of holding
  references that go stale, everything is read fresh from the DOM on each
  trigger, and a MutationObserver notices when the element identity changes and
  re-runs the conversion pass. `data-threshold` always holds the pristine
  shop-currency amount and is converted on read, never written back, so
  repeated passes cannot compound.
*/

(function cartProgress() {
  const ROOT = '[data-cart-progress]';
  const REFRESH_DELAY = 150;

  let currentRoot = null;
  let hasFreeGift = false;
  let refreshTimer = null;
  let refreshInFlight = null;
  let refreshQueued = false;

  const getRoot = () => document.querySelector(ROOT);

  const getParts = (root) => ({
    fill: root.querySelector('[data-cart-progress-fill]'),
    message: root.querySelector('[data-cart-progress-message]'),
    markers: Array.from(root.querySelectorAll('[data-cart-progress-marker]')),
  });

  /* ------------------------------------------------------------------ *
   * Reading state
   * ------------------------------------------------------------------ */

  function conversionRate(root) {
    if (root.dataset.needsConversion !== 'true') return 1;

    const rate = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate);
    return rate && isFinite(rate) && rate > 0 ? rate : 1;
  }

  /**
   * Tier data lives on the markers, so there is no separate payload to keep in
   * sync. Thresholds are converted here on every read — a per-country override
   * is already stated in that market's currency, so it is left alone.
   *
   * The returned list is ordered by AMOUNT, and positions are handed out by
   * rank, exactly as note 6 in cart-progress-bar.liquid describes. Liquid does
   * the same thing server side, so why again here?
   *
   * Because Liquid orders on the amounts IT can see, and for a converted tier
   * that is the unconverted one. Mixing a fixed override (already in the
   * customer's currency) with a converted campaign means the two can rank
   * differently before and after the rate is applied. Concretely, with a gift
   * campaign of 100 EUR and free shipping fixed at CHF 100, Liquid compares
   * 10000 against 10000 and keeps shipping first, while the shopper actually
   * reaches the gift at CHF 96.59 — first. Liquid cannot know that; the rate
   * does not exist in Liquid.
   *
   * So the server order is a first-paint approximation and this is the
   * authority. Wherever the two agree — every market whose rate is 1, and any
   * market where the amounts are not close enough to swap — this is a no-op.
   *
   * `data-threshold` and `data-position` are both left pristine. Everything is
   * derived from them on each read and never written back, so repeated passes
   * cannot accumulate drift.
   */
  function readTiers(root, markers) {
    const rate = conversionRate(root);

    const tiers = markers.map((marker) => {
      const base = Number(marker.dataset.threshold);
      const fixed = marker.dataset.fixed === 'true';

      return {
        marker,
        threshold: fixed ? base : Math.round(base * rate),
        position: Number(marker.dataset.position),
        isGift: marker.dataset.isGift === 'true',
        message: marker.dataset.message,
      };
    });

    // Take the slots off the markers BEFORE reordering — the set of positions
    // belongs to the track, not to whichever reward currently occupies one.
    const slots = tiers.map((tier) => tier.position).sort((a, b) => a - b);

    tiers.sort((a, b) => a.threshold - b.threshold);
    tiers.forEach((tier, index) => {
      tier.position = slots[index];
    });

    return tiers;
  }

  /**
   * Move a marker to the slot the sort gave it. Mirrors the alignment rule in
   * the Liquid marker loop so the outermost label stays inside the track.
   *
   * Skipped when the slot is the one Liquid already rendered, which is the
   * usual case — that keeps the server's markup untouched rather than
   * overwriting it with an identical value.
   */
  function applyPosition(marker, position) {
    if (!marker || !isFinite(position)) return;
    if (Number(marker.dataset.position) === position) return;

    marker.style.setProperty('--cp-marker-position', `${position}%`);
    marker.dataset.align = position >= 100 ? 'end' : position <= 0 ? 'start' : 'center';
  }

  /**
   * Mirrors the Liquid rule (see the gift detection block in
   * cart-progress-bar.liquid). "Costs nothing" alone is not enough — any 100%
   * discounted line would match, which would unlock the gift milestone off an
   * unrelated discount. The line must also be identifiable as the app's gift:
   * either it IS the configured gift product, or it carries the campaign's
   * named discount.
   */
  function isGiftLine(root, item) {
    if (item.final_line_price !== 0 || !(item.original_line_price > 0)) return false;

    const giftProductId = Number(root.dataset.giftProductId);
    if (giftProductId && Number(item.product_id) === giftProductId) return true;

    const token = (root.dataset.giftDiscountTitle || '').trim().toLowerCase();
    if (!token) return false;

    const allocations = item.line_level_discount_allocations || [];

    return allocations.some((allocation) =>
      String((allocation.discount_application || {}).title || '')
        .toLowerCase()
        .includes(token)
    );
  }

  function detectFreeGift(root, items) {
    return (items || []).some((item) => isGiftLine(root, item));
  }

  /** Discounty uses product-line value after price-changing discounts. */
  /**
   * Every line of the configured gift product, discounted or not.
   *
   * Deliberately different from isGiftLine above. That one answers "is this the
   * gift, free, right now" and is what the completed state keys on. This one
   * answers "is this line the gift product at all", which still holds after
   * Discounty has taken its discount away. Still needed with enforcement gone:
   * cartAmount subtracts these lines so the bar cannot count a gift reverted to
   * full price as progress towards earning that same gift.
   */
  function giftLines(root, items) {
    const giftProductId = Number(root.dataset.giftProductId);
    if (!giftProductId) return [];

    return (items || []).filter((item) => Number(item.product_id) === giftProductId);
  }

  /**
   * Discounty uses product-line value after price-changing discounts.
   *
   * The gift product's own line is excluded from that. While the gift is free it
   * contributes 0 anyway, so this changes nothing in the normal case — but when
   * Discounty drops the discount the line reverts to full price (1000 EUR on
   * this store), and counting it would report a cart that qualifies purely on
   * the strength of the reward it is supposed to have earned. The bar would sit
   * at 100% and the gift would never be recognised as unearned.
   */
  function cartAmount(root, cartData) {
    const total = cartData.items_subtotal_price;
    if (typeof total !== 'number') return total;

    return giftLines(root, cartData.items).reduce((sum, item) => sum - item.final_line_price, total);
  }

  /* ------------------------------------------------------------------ *
   * Formatting
   * ------------------------------------------------------------------ */

  function formatMoney(root, cents) {
    const currency = root.dataset.presentmentCurrency;
    const locale = root.dataset.locale || document.documentElement.lang || 'en';

    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
    } catch (error) {
      return `${(cents / 100).toFixed(2)} ${currency}`;
    }
  }

  /** Turns `**bold**` copy with an [amount] placeholder into safe HTML. */
  function buildMessage(root, template, amountCents) {
    const holder = document.createElement('div');
    holder.textContent = String(template || '').replace('[amount]', formatMoney(root, amountCents));

    return holder.innerHTML
      .split('**')
      .map((chunk, index) => (index % 2 === 1 ? `<strong>${chunk}</strong>` : chunk))
      .join('');
  }

  /* ------------------------------------------------------------------ *
   * Rendering — one path for every caller
   * ------------------------------------------------------------------ */

  /**
   * Walks the tiers in order, lighting up the ones reached and interpolating the
   * fill inside the segment the cart currently sits in.
   *
   * `updateCopy` is only true when the numbers exist client side (a converted
   * market). In the shop currency Liquid already rendered the correct copy and
   * we must not overwrite it with a JS-formatted approximation.
   */
  function render(root, total, updateCopy) {
    if (!isFinite(total)) return;

    const { fill, message, markers } = getParts(root);
    const tiers = readTiers(root, markers);

    let previousThreshold = 0;
    let previousPosition = 0;
    let percent = 100;
    let resolved = false;
    let activeMessage = root.dataset.completeMessage;
    let remaining = 0;

    tiers.forEach((tier) => {
      const reached = total >= tier.threshold || (tier.isGift && hasFreeGift);

      // `tiers` is ordered by amount and `markers` is in DOM order. Those are
      // the same list until a conversion reorders them, so the marker has to
      // come off the tier — indexing into `markers` here would light up the
      // wrong milestone in exactly the case this sort exists to handle.
      const marker = tier.marker;
      if (marker) {
        marker.classList.toggle('is-reached', reached);
        applyPosition(marker, tier.position);
      }

      if (reached) {
        previousThreshold = tier.threshold;
        previousPosition = tier.position;
        return;
      }

      // The first unreached tier is the one we are working towards.
      if (resolved) return;
      resolved = true;
      remaining = tier.threshold - total;
      activeMessage = tier.message;

      const span = tier.threshold - previousThreshold;
      percent =
        span > 0
          ? previousPosition + ((total - previousThreshold) / span) * (tier.position - previousPosition)
          : previousPosition;
    });

    /*
      Mirror of the Liquid rule: every threshold cleared is not the same as
      every reward being in the cart. The gift tier lights up on amount alone,
      but the app's popup is what adds the free product, so until it lands the
      bar is full and the gift is still missing — saying "you have received all
      your free gifts" there is simply false.
    */
    const giftTierExists = tiers.some((tier) => tier.isGift);
    const allRewardsClaimed = !resolved && !(giftTierExists && !hasFreeGift);

    if (!resolved && !allRewardsClaimed) {
      activeMessage = root.dataset.giftReadyMessage;
    }

    const clamped = Math.min(100, Math.max(0, percent));
    root.classList.toggle('cart-progress--complete', allRewardsClaimed);

    if (fill) {
      fill.style.setProperty('--cp-progress', `${clamped}%`);
      fill.setAttribute('aria-valuenow', String(Math.round(clamped)));
      // Every tier reached — only then may the fill run under the last icon.
      fill.toggleAttribute('data-filled', !resolved);
    }

    if (updateCopy && message) {
      message.innerHTML = buildMessage(root, activeMessage, remaining);
    }

    renderGift(root, tiers, total);
    syncGiftGate(root, total);
  }

  /**
   * Keeps the gift teaser in step: revealed once the tier before the gift tier
   * is cleared, locked until the gift tier itself is cleared, and gone once the
   * gift is in the cart.
   */
  function renderGift(root, tiers, total) {
    const gift = root.querySelector('[data-cart-progress-gift]');
    if (!gift) return;

    const giftIndex = tiers.findIndex((tier) => tier.isGift);
    let state = 'hidden';

    if (giftIndex >= 0 && !hasFreeGift) {
      const reached = total >= tiers[giftIndex].threshold;
      const previousReached = giftIndex === 0 || total >= tiers[giftIndex - 1].threshold;

      if (reached) state = 'unlocked';
      else if (previousReached) state = 'locked';
    }

    gift.hidden = state === 'hidden';
    gift.classList.toggle('is-locked', state === 'locked');
    gift.classList.toggle('is-unlocked', state === 'unlocked');
  }

  /**
   * The free-gift PDP gate.
   *
   * A product on templates/product.free-gift is never sold on its own, so
   * snippets/buy-buttons.liquid renders its add-to-cart button `disabled` and
   * tags it `[data-free-gift-gate]`. Liquid cannot resolve the lock there: the
   * threshold is a cart-drawer BLOCK setting and sections cannot read each
   * other's settings. It can be resolved here, because the drawer — and with it
   * the progress bar — is rendered on every page, so the gift marker carrying
   * `data-threshold` is already in this document.
   *
   * Reading the threshold off that marker rather than duplicating it into the
   * product section means there is one source of truth, and the per-market
   * conversion in readTiers applies for free.
   *
   * The button lives outside the bar, so this queries from `document`.
   */
  const getGiftGateButtons = () => document.querySelectorAll('[data-free-gift-gate]');

  /**
   * Write only on change — this is load-bearing, not a micro-optimisation.
   *
   * Assigning textContent drops the existing text node and inserts a new one,
   * which is a childList mutation inside document.body — precisely what the
   * MutationObserver at the bottom of this file watches. An unconditional write
   * from a path that observer can reach re-triggers the observer, which writes
   * again: the tab locks up. Comparing first makes the write a no-op once the
   * label already says the right thing, so the loop cannot start.
   */
  function setText(element, value) {
    if (element && value && element.textContent !== value) element.textContent = value;
  }

  /**
   * Shuts the gate with no bar to measure against.
   *
   * Needed because cart-drawer.liquid renders the bar inside the `else` of its
   * `cart == empty` check, so emptying the cart takes the whole element out of
   * the DOM — and every trigger below starts with getRoot(). Without this the
   * button kept whatever state it last had, which after a qualifying cart meant
   * enabled: clear the cart from an unlocked gift PDP and the gift stayed
   * addable at nothing in the basket.
   *
   * An empty cart cannot have cleared the threshold, so locked is not merely the
   * safe answer here, it is the right one. Same for a storefront with the bar
   * switched off altogether: no campaign UI, no unlock.
   *
   * The amount is deliberately dropped from the label — the threshold lives on
   * the bar, so with the bar gone there is no figure to quote, and the plain
   * locked copy is what Liquid renders on an empty-cart page load anyway.
   */
  function lockGiftGate() {
    getGiftGateButtons().forEach((button) => {
      button.disabled = true;
      button.classList.add('is-free-gift-locked');

      setText(button.querySelector('[data-free-gift-gate-label]'), button.dataset.lockedLabel);
    });
  }

  function syncGiftGate(root, total) {
    const buttons = getGiftGateButtons();
    if (!buttons.length) return;

    const giftTier = readTiers(root, getParts(root).markers).find((tier) => tier.isGift);
    const amount = isFinite(total) ? total : Number(root.dataset.cartTotal);

    // Having the gift already keeps the button shut too — the reward is one per
    // cart, and an enabled button there would invite a second, paid one.
    const unlocked =
      !!giftTier && !hasFreeGift && isFinite(amount) && amount >= giftTier.threshold;

    buttons.forEach((button) => {
      button.disabled = !unlocked;
      button.classList.toggle('is-free-gift-locked', !unlocked);

      const label = button.querySelector('[data-free-gift-gate-label]');
      if (!label) return;

      if (unlocked) {
        setText(label, button.dataset.unlockedLabel);
        return;
      }

      // Only name the amount while it is still something to work towards.
      // Once the gift is in the cart the threshold is behind the shopper and
      // quoting it would read as a second, unmet condition.
      const template = hasFreeGift ? '' : button.dataset.lockedAmountLabel;

      setText(
        label,
        template && giftTier
          ? template.replace('[amount]', formatMoney(root, giftTier.threshold))
          : button.dataset.lockedLabel
      );
    });
  }

  /* ------------------------------------------------------------------ *
   * Gift eligibility
   * ------------------------------------------------------------------ */

  /*
    Enforcement is OFF (GSD-65, 2026-09-15).

    Discounty support confirmed their campaign now removes the unearned gift
    itself, so the theme stepping in is redundant at best and, while both were
    acting on the same cart, was tripping their "shopper declined the gift"
    latch. Handed back to the app.

    Left switched off rather than deleted, because we are taking the vendor's
    word for it and this is the second account they have given. `?cp_gift_enforce=on`
    restores it for a tab; flipping the default below restores it for everyone.
    What it guards against is a 1000 EUR line sitting in a cart at full price,
    which is not a failure to discover in production a second time.

    `?cp_debug=1` narrates the gift lifecycle to the console — every cart
    mutation with the script that made it, and every enforcement decision. Both
    flags stick to the tab, because the repro survives reloads and a dropped
    flag reads as a clean run when it was really an untested one.
  */
  const flags = (() => {
    const params = new URLSearchParams(window.location.search);

    return (param, key) => {
      const value = params.get(param);

      try {
        if (value !== null) sessionStorage.setItem(key, value);
        return sessionStorage.getItem(key);
      } catch (error) {
        return value;
      }
    };
  })();

  // const GIFT_ENFORCEMENT_ENABLED = flags('cp_gift_enforce', 'cpGiftEnforce') === 'on';
  const CART_PROGRESS_DEBUG = flags('cp_debug', 'cpDebug') === '1';

  function debugLog(...args) {
    if (!CART_PROGRESS_DEBUG) return;
    console.log(`[cart-progress t+${Math.round(performance.now())}ms]`, ...args);
  }

  if (CART_PROGRESS_DEBUG) {
    debugLog('debug on, gift enforcement commented out');
  }

  /**
   * The first stack frame outside this file — the script that made the call.
   *
   * This is the whole point of the logging: a cart mutation attributed to
   * discounty/sdk.js is the app acting, one attributed to cart.js or this file
   * is the theme. Best-effort, and format varies by browser, so it is a hint
   * for a human reading a timeline rather than anything to branch on.
   */
  function traceCaller() {
    if (!CART_PROGRESS_DEBUG) return '';

    const stack = String((new Error().stack || '')).split('\n').slice(2);
    const frame = stack.find((line) => line.indexOf('cart-progress.js') === -1) || stack[0] || '';

    return `called from: ${frame.trim()}`;
  }

  /* ==================================================================== *
   *  GIFT ENFORCEMENT — COMMENTED OUT 2026-09-15 (GSD-65)
   *
   *  Discounty support (Harry) confirmed their campaign now adds AND removes
   *  the gift itself, and asked for the theme's enforcement to be disabled
   *  entirely. Verified on theme 201841410312 over eight add/remove cycles
   *  with this code inert: the app added the gift at 0 EUR on every crossing
   *  and removed it about 1.4s after the cart dropped below the threshold.
   *  The session-wide "gift stops being offered" failure did not reproduce.
   *
   *  Left in place, commented, rather than deleted. It is the only thing that
   *  ever stopped a 1000 EUR gift line sitting in a cart at full price, and
   *  this is the second account the vendor has given of how their app behaves.
   *  To restore: uncomment this block and the two scheduleGiftEnforcement()
   *  call sites in init() and onCartUpdate(), plus GIFT_ENFORCEMENT_ENABLED
   *  above. Note the removal below targets the line item KEY, not the position
   *  — do not "simplify" it back to updateQuantity(line, 0), which resolves a
   *  position server-side and can take out a product the shopper chose.
   * ==================================================================== */

  // let giftEnforcementInFlight = false;
  // let giftEnforcementAttempts = 0;
  // let giftEnforcementTimer = null;
  // const GIFT_ENFORCEMENT_MAX_ATTEMPTS = 3;
  //
  // The app removes the unearned gift itself now, so this file is no longer the
  // thing that does it — it is the backstop for when the app does not.
  //
  // Both acting on the same cart is what broke the flow: the theme got there
  // first, the app's own removal then failed against a line that was already
  // gone, and the app read that failure as the shopper declining the gift and
  // stopped offering it for the rest of the session.
  //
  // So wait, and let the app go first. Its window is around 1.5s on the vendor's
  // own measurement; this sits clear of it. Through that window an unearned gift
  // sits in the cart at full price — that gap is the cost of handing the job
  // over, and closing it is the app's side of the deal.
  //
  // const GIFT_ENFORCEMENT_DELAY = 3000;

  // Takes the gift back out of the cart when the cart no longer earns it.
  //
  // The state this fixes: Discounty drops the gift in at the threshold, the
  // shopper then removes the products that got them there, and Discounty
  // withdraws its discount — but leaves the line. The gift silently becomes a
  // 1000 EUR purchase sitting in the cart.
  //
  // Only the gift product configured on the bar is ever touched, and only while
  // the cart is short of the gift threshold. That product exists to be given
  // away (that is the whole free-gift template), so removing it is never
  // removing something the shopper chose to buy. If the product is ever sold
  // normally, this has to go.
  //
  // The removal targets the line item KEY, not its position. updateQuantity
  // sends a 1-based POSITION that Shopify resolves against the cart as it
  // stands when the request lands; with the app editing the same cart, a
  // removal landing between our read and our write shifts every later line up
  // one and the position we read points at a product the shopper chose. A line
  // item key cannot shift.
  //
  // function enforceGiftEligibility(root, cartData) {
  //   if (giftEnforcementInFlight || !cartData) {
  //     debugLog('check skipped', { inFlight: giftEnforcementInFlight, hasCartData: !!cartData });
  //     return;
  //   }
  //
  //   const giftTier = readTiers(root, getParts(root).markers).find((tier) => tier.isGift);
  //   if (!giftTier) {
  //     debugLog('check skipped — no gift tier configured on the bar');
  //     return;
  //   }
  //
  //   const items = cartData.items || [];
  //   const giftItem = items.find((item) => giftLines(root, [item]).length);
  //
  //   // No gift in the cart, or the cart genuinely earns it. Either way the
  //   // shopper is in a good state, so let a later dip try again from scratch.
  //   if (!giftItem) {
  //     debugLog('no gift line in cart — nothing to do (the app got there first)');
  //     giftEnforcementAttempts = 0;
  //     return;
  //   }
  //
  //   // Without a key there is no safe way to name the line, and naming it by
  //   // position is the thing this function refuses to do.
  //   if (!giftItem.key) {
  //     debugLog('gift line has no key — refusing to remove by position');
  //     return;
  //   }
  //
  //   const qualifying = cartAmount(root, cartData);
  //   if (!isFinite(qualifying) || qualifying >= giftTier.threshold) {
  //     debugLog('gift present and earned — leaving it', {
  //       qualifying,
  //       threshold: giftTier.threshold,
  //     });
  //     giftEnforcementAttempts = 0;
  //     return;
  //   }
  //
  //   debugLog('UNEARNED GIFT STILL PRESENT after the wait — the app did not remove it', {
  //     key: giftItem.key,
  //     product: giftItem.product_title,
  //     qualifying,
  //     threshold: giftTier.threshold,
  //     attempts: giftEnforcementAttempts,
  //   });
  //
  //   // Discounty re-injects its rewards on cart mutations, so a campaign that
  //   // disagrees with this check would have us removing and it re-adding without
  //   // end. Cap the attempts: a genuine unearned gift goes on the first pass, and
  //   // a fight with the app stops after three instead of hammering /cart/change.
  //   if (giftEnforcementAttempts >= GIFT_ENFORCEMENT_MAX_ATTEMPTS) {
  //     debugLog('attempt cap reached — standing down until the cart recovers');
  //     return;
  //   }
  //
  //   giftEnforcementAttempts += 1;
  //   giftEnforcementInFlight = true;
  //
  //   debugLog('>>> THEME IS REMOVING THE GIFT', {
  //     key: giftItem.key,
  //     product: giftItem.product_title,
  //     attempt: giftEnforcementAttempts,
  //   });
  //
  //   const changeUrl = (window.routes && window.routes.cart_change_url) || '/cart/change';
  //
  //   fetch(changeUrl, {
  //     method: 'POST',
  //     headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  //     body: JSON.stringify({ id: giftItem.key, quantity: 0 }),
  //   })
  //     .then((response) => {
  //       // A non-OK response here is the good case as often as the bad one: it
  //       // is what Shopify says when the key is gone, i.e. when the app removed
  //       // the gift between our read and this write.
  //       debugLog('removal responded', response.status);
  //     })
  //     .catch((error) => {
  //       debugLog('removal failed', error);
  //     })
  //     .then(() => {
  //       giftEnforcementInFlight = false;
  //
  //       // Hand the re-render back to Dawn. CartItems.onCartUpdate refetches the
  //       // cart-drawer section for any source but its own.
  //       if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
  //         publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-progress' });
  //       } else {
  //         scheduleRefresh();
  //       }
  //     });
  // }
  //
  // Runs the eligibility check once the app has had its turn. Always against a
  // freshly read cart, never the caller's snapshot: after the wait that
  // snapshot describes a cart that no longer exists. One timer, restarted by
  // every cart update, so a burst of changes settles into a single check.
  //
  // function scheduleGiftEnforcement(root) {
  //   if (!root.dataset.giftProductId) return;
  //
  //   debugLog(giftEnforcementTimer ? 'enforcement re-armed (timer restarted)' : 'enforcement armed', {
  //     firesInMs: GIFT_ENFORCEMENT_DELAY,
  //   });
  //
  //   clearTimeout(giftEnforcementTimer);
  //
  //   giftEnforcementTimer = setTimeout(() => {
  //     giftEnforcementTimer = null;
  //
  //     const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
  //
  //     debugLog('wait elapsed — re-reading the cart');
  //
  //     fetch(`${cartUrl}.js`)
  //       .then((response) => response.json())
  //       .then((cartData) => {
  //         const current = getRoot();
  //         if (current) enforceGiftEligibility(current, cartData);
  //         else debugLog('bar is gone — no check this pass');
  //       })
  //       .catch(() => {
  //         /* a failed read just means no enforcement this pass */
  //       });
  //   }, GIFT_ENFORCEMENT_DELAY);
  // }

  /* ------------------------------------------------------------------ *
   * Triggers
   * ------------------------------------------------------------------ */

  /*
    The last marker is pinned to the right edge but its icon is centred over the
    label, so the icon sits well inside the track. The CSS stops an unfinished
    fill at that icon's left edge; this measures the distance, which depends on
    the label's rendered width and therefore cannot be known in CSS.
    Re-measured on resize: fonts load late and the drawer may still be closed
    (zero width) when the bar first appears.
  */
  const END_ICON_GAP = 2;
  let endInsetObserver = null;

  function measureEndInset(wrapper) {
    const icon = wrapper.querySelector('[data-cart-progress-marker][data-align="end"] .cart-progress__icon');
    const track = wrapper.getBoundingClientRect();
    if (!icon || !track.width) return;

    const inset = track.right - icon.getBoundingClientRect().left + END_ICON_GAP;
    wrapper.style.setProperty('--cp-end-inset', `${Math.max(0, Math.round(inset))}px`);
  }

  function watchEndInset(root) {
    const wrapper = root.querySelector('[data-cart-progress-fill]');
    if (!wrapper) return;

    measureEndInset(wrapper);

    if (typeof ResizeObserver !== 'function') return;
    if (endInsetObserver) endInsetObserver.disconnect();
    endInsetObserver = new ResizeObserver(() => measureEndInset(wrapper));
    endInsetObserver.observe(wrapper);
  }

  /** Runs whenever a freshly rendered bar appears in the DOM. */
  function init(root) {
    currentRoot = root;
    watchEndInset(root);

    // Liquid already ran the full detection over cart.items server-side, so
    // take its answer. Deriving it from a reached gift marker instead would be
    // wrong: that marker is also set by simply crossing the threshold.
    hasFreeGift = root.dataset.hasFreeGift === 'true';

    // Ahead of the currency check: the gate has to resolve in every market,
    // and in the shop currency render() below never runs.
    syncGiftGate(root, Number(root.dataset.cartTotal));

    // A cart can already hold an unearned gift on arrival — the shopper emptied
    // it in another tab, or left and came back. Liquid cannot mutate the cart,
    // so the check had to run from here as well as off cart updates. Handed to
    // the app along with the rest of the enforcement.
    //
    // scheduleGiftEnforcement(root);

    // In the shop currency the Liquid render is already correct and complete.
    if (root.dataset.needsConversion !== 'true') return;

    render(root, Number(root.dataset.cartTotal), true);
  }

  function onCartUpdate(event) {
    const root = getRoot();

    // No bar means the cart went empty and the drawer swapped in its empty
    // branch. There is nothing to render, but the gate still has to be shut.
    if (!root) {
      lockGiftGate();
      return Promise.resolve();
    }

    const cartData = event && event.cartData;

    if (cartData) {
      const total = cartAmount(root, cartData);

      if (typeof total === 'number') {
        root.dataset.cartTotal = String(total);
        hasFreeGift = detectFreeGift(root, cartData.items);
        root.dataset.hasFreeGift = hasFreeGift ? 'true' : 'false';
        // Move the bar in the same frame as the change. Copy is only rewritten
        // where the server could not have known the converted amount.
        render(root, total, root.dataset.needsConversion === 'true');
      }

      if (CART_PROGRESS_DEBUG) {
        const giftTier = readTiers(root, getParts(root).markers).find((tier) => tier.isGift);
        const present = giftLines(root, cartData.items);

        // The one line to read while testing: is the gift still in the cart,
        // is it still free, and does the cart still earn it.
        debugLog('cart update', {
          qualifyingTotal: total,
          giftThreshold: giftTier && giftTier.threshold,
          giftLinesInCart: present.length,
          giftAtFullPrice: present.some((item) => item.final_line_price > 0),
          earnsGift: giftTier ? total >= giftTier.threshold : null,
          itemCount: cartData.item_count,
        });
      }

      // This was the path that mattered for enforcement: removing a line is a
      // Dawn cart-items action, so the cart that just stopped earning the gift
      // arrived here. The bar still moves on this snapshot; the removal is the
      // app's job now.
      //
      // scheduleGiftEnforcement(root);
    }

    // Dawn re-renders the whole drawer for its own flows, so a fetch here would
    // be a duplicate against markup that is about to be replaced. That includes
    // the one watchCartRequests already queued for this same mutation — seeing
    // Dawn's own event is proof its chain is intact, which is the only thing
    // that fallback exists to cover, so drop it.
    const source = event && event.source;
    if (source === 'cart-items' || source === 'product-form') {
      cancelRefresh();
      return Promise.resolve();
    }

    return refreshFromSection();
  }

  function cancelRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }

  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refreshFromSection, REFRESH_DELAY);
  }

  /**
   * True when a cart mutation is adding the gift variant.
   *
   * The app's popup owns adding the gift, and it opens the drawer before the
   * theme has re-rendered: for the best part of a second the drawer was visible
   * with the teaser card still in it, then the teaser vanished and a real cart
   * line for the same product took its place a few pixels lower. Recognising
   * the request lets the teaser go straight away so the two never overlap.
   *
   * Optimistic, and safe to be: the refresh that follows re-reads the verdict
   * from Liquid, so a failed add corrects itself.
   */
  function isGiftAdd(url, body) {
    if (!/\/cart\/add(\.js)?(\?|$)/.test(String(url || ''))) return false;

    const gift = document.querySelector('[data-cart-progress-gift]');
    const variantId = gift && gift.dataset.variantId;
    if (!variantId) return false;

    let text = String(url);

    if (typeof body === 'string') {
      text += body;
    } else if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      text += body.toString();
    } else if (typeof FormData !== 'undefined' && body instanceof FormData) {
      // FormData has no serialiser, and the id can sit under items[0][id].
      body.forEach((value, key) => {
        text += `${key}=${value}&`;
      });
    }

    return text.indexOf(variantId) !== -1;
  }

  function markGiftClaimed() {
    const root = getRoot();
    if (!root || hasFreeGift) return;

    hasFreeGift = true;
    root.dataset.hasFreeGift = 'true';

    /*
      Copy is rewritten here even in the shop currency, where render() otherwise
      leaves it to Liquid. The bar has just gone complete, so the only message it
      can pick is the complete one and the amount still missing is zero — there
      is no money for a JS approximation to get wrong. Skipping it left the card
      and the class updating instantly while the text still read "pick your gift"
      for the half second until the refresh landed.
    */
    render(root, Number(root.dataset.cartTotal), true);
  }

  /*
    The last line of defence, and on this store the important one.

    Both window.fetch and XMLHttpRequest.prototype.send are already overridden
    by the installed apps (the free-gift app logs "fetch-override:/cart/change").
    Dawn re-renders the drawer inside the .then() of its own cart fetch and
    publishes cartUpdate from the same chain, so if an app's wrapper ever fails
    to pass the response through, Dawn updates nothing AND emits no event — the
    bar is left with no signal at all.

    So rather than trusting that chain, watch for requests to the cart mutation
    endpoints whoever makes them, and refresh from Liquid once they settle. An
    app adding its gift is itself a /cart/change call, so a follow-up mutation
    triggers another refresh and the bar converges on its own.
  */
  function watchCartRequests() {
    const isCartMutation = (url) => /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(String(url || ''));

    const originalFetch = window.fetch;
    if (typeof originalFetch === 'function') {
      window.fetch = function patchedFetch(input, init) {
        const url = (input && input.url) || input;
        const result = originalFetch.apply(this, arguments);

        if (isCartMutation(url)) {
          // Who issued it matters more than what it did: a /cart/change the
          // theme did not log a removal for immediately before is the app's.
          debugLog('cart mutation (fetch)', String(url), traceCaller());
          if (isGiftAdd(url, init && init.body)) markGiftClaimed();
          // Never let our bookkeeping alter the caller's promise.
          Promise.resolve(result).then(scheduleRefresh, scheduleRefresh);
        }

        return result;
      };
    }

    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function patchedOpen(method, url) {
      this.__cartProgressUrl = url;
      return originalOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function patchedSend(body) {
      if (isCartMutation(this.__cartProgressUrl)) {
        debugLog('cart mutation (xhr)', String(this.__cartProgressUrl), traceCaller());
        if (isGiftAdd(this.__cartProgressUrl, body)) markGiftClaimed();
        this.addEventListener('loadend', scheduleRefresh);
      }
      return originalSend.apply(this, arguments);
    };
  }

  /**
   * Pulls a freshly rendered bar from Liquid so the copy, the localised money
   * amount and the thresholds stay authoritative.
   */
  function refreshFromSection() {
    refreshTimer = null;

    /*
      One refresh in the air at a time. A single gift add produced two of these
      a second apart, and two overlapping fetches can also land out of order and
      write stale markup over fresh. Anything that changed while one was flying
      is picked up by a single follow-up instead.
    */
    if (refreshInFlight) {
      refreshQueued = true;
      return refreshInFlight;
    }

    const root = getRoot();

    if (!root) {
      lockGiftGate();
      return Promise.resolve();
    }

    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    const sectionId = root.dataset.sectionId || 'cart-drawer';

    refreshInFlight = fetch(`${cartUrl}?section_id=${sectionId}`)
      .then((response) => response.text())
      .then((responseText) => {
        const source = new DOMParser().parseFromString(responseText, 'text/html').querySelector(ROOT);
        const target = getRoot();
        if (!target) return;

        // No bar in the fresh render means the cart emptied (or the feature was
        // switched off), so the stale one must not be left behind.
        if (!source) {
          target.remove();
          currentRoot = null;
          return;
        }

        target.className = source.className;
        target.dataset.cartTotal = source.dataset.cartTotal;
        target.dataset.completeMessage = source.dataset.completeMessage;

        // The fresh render re-ran gift detection over the real cart, so adopt
        // its verdict. Leaving the cached flag behind would let a later
        // conversion pass re-show a gift card that is already in the cart.
        target.dataset.hasFreeGift = source.dataset.hasFreeGift;
        hasFreeGift = source.dataset.hasFreeGift === 'true';

        const sourceParts = getParts(source);
        const targetParts = getParts(target);

        if (targetParts.message && sourceParts.message) {
          targetParts.message.innerHTML = sourceParts.message.innerHTML;
        }

        targetParts.markers.forEach((marker, index) => {
          const from = sourceParts.markers[index];
          if (!from) return;

          marker.className = from.className;
          marker.dataset.align = from.dataset.align;
          marker.dataset.threshold = from.dataset.threshold;
          marker.dataset.position = from.dataset.position;
          marker.dataset.isGift = from.dataset.isGift;
          marker.dataset.fixed = from.dataset.fixed;
          marker.dataset.message = from.dataset.message;
          marker.style.setProperty('--cp-marker-position', `${from.dataset.position}%`);
        });

        const sourceGift = source.querySelector('[data-cart-progress-gift]');
        const targetGift = target.querySelector('[data-cart-progress-gift]');
        if (sourceGift && targetGift) {
          targetGift.replaceWith(sourceGift.cloneNode(true));
        } else if (targetGift && !sourceGift) {
          targetGift.remove();
        }

        if (targetParts.fill && sourceParts.fill) {
          targetParts.fill.style.setProperty(
            '--cp-progress',
            sourceParts.fill.style.getPropertyValue('--cp-progress')
          );
          targetParts.fill.setAttribute('aria-valuenow', sourceParts.fill.getAttribute('aria-valuenow') || '0');
          targetParts.fill.toggleAttribute('data-filled', sourceParts.fill.hasAttribute('data-filled'));
        }

        // The markup just pulled in carries shop-currency thresholds, so redo
        // the conversion pass on top of it.
        init(target);
      })
      .catch((error) => {
        console.error('cart-progress: refresh failed', error);
      })
      .then(() => {
        refreshInFlight = null;

        if (refreshQueued) {
          refreshQueued = false;
          scheduleRefresh();
        }
      });

    return refreshInFlight;
  }

  /* ------------------------------------------------------------------ *
   * Wiring
   * ------------------------------------------------------------------ */

  function start() {
    const root = getRoot();
    if (root) {
      init(root);
    } else {
      // Landing straight on a gift PDP with an empty cart: Liquid already
      // rendered the button locked, this keeps it that way on a page where the
      // bar never appears.
      lockGiftGate();
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
    }

    document.addEventListener('cart:refresh', scheduleRefresh);
    watchCartRequests();

    // Delegated so it survives the card being replaced on every refresh.
    document.addEventListener('click', (event) => {
      const button = event.target.closest && event.target.closest('[data-cart-progress-gift-add]');
      if (!button) return;

      const gift = button.closest('[data-cart-progress-gift]');
      const variantId = gift && gift.dataset.variantId;
      if (!variantId || button.disabled) return;

      button.disabled = true;
      const body = new FormData();
      body.append('id', variantId);
      body.append('quantity', '1');

      // watchCartRequests picks the /cart/add up and refreshes from Liquid.
      fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`, { method: 'POST', body })
        .catch((error) => console.error('cart-progress: gift add failed', error))
        .then(() => {
          button.disabled = false;
        });
    });

    /*
      Stands in for the custom-element lifecycle we gave up. Two distinct cases:

      - the bar element itself was swapped out, meaning Dawn re-rendered the
        drawer with fresh Liquid. Only the conversion pass is needed.
      - only the item list changed, meaning an app mutated the cart without
        going through the theme. That needs a refetch to learn the new totals.
    */
    new MutationObserver((mutations) => {
      const root = getRoot();

      // The bar just left the DOM — the drawer re-rendered into its empty
      // branch. This is the path that actually fires when the shopper removes
      // the last line, so it is the one that has to shut the gate.
      if (!root) {
        currentRoot = null;
        lockGiftGate();
        return;
      }

      if (root !== currentRoot) {
        init(root);
        return;
      }

      const itemsTouched = mutations.some(
        (mutation) => mutation.target.closest && mutation.target.closest('cart-drawer-items')
      );
      if (itemsTouched) scheduleRefresh();
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();

/*
  The Discounty / free-gift apps inject their own progress widgets into the cart
  drawer after the theme has rendered, and re-inject them on every cart update.
  A static stylesheet rule is not enough on its own, so the matched nodes are
  also hidden imperatively as they appear.
*/
(function hideAppProgressBars() {
  const config = window.cartProgressHideSelectors;

  if (!config || !config.length) return;

  const selector = config.join(', ');

  const hide = (root) => {
    if (!root || root.nodeType !== 1) return;

    if (root.matches && root.matches(selector)) {
      root.setAttribute('data-cart-progress-hidden', '');
      return;
    }

    if (!root.querySelectorAll) return;

    root.querySelectorAll(selector).forEach((node) => node.setAttribute('data-cart-progress-hidden', ''));
  };

  const start = () => {
    hide(document.body);

    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => mutation.addedNodes.forEach(hide));
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
